import { type ReactNode, type Ref } from 'react'
import useEvent from 'react-use-event-hook'
import { Accordion as BaseAccordion } from '@base-ui/react/accordion'

import { cn } from '@gto/ui/cn'

import { type AccordionSize, type AccordionVariant, accordionVariants } from './accordion.styles'
import {
  AccordionSizeContext,
  AccordionVariantContext,
  SINGLE_ITEM_VALUE,
} from './accordion-context'

export type RootProps = {
  /**
   * Visual variant.
   *
   * - `inline` (default): title sits OUTSIDE an elevated content panel. Used by
   *   the trainer-settings "how to use…" helpers.
   * - `card`: title and content live INSIDE a bordered paper card; the whole
   *   card surface is the clickable trigger. Variant is propagated to `Trigger`
   *   and `Content` via context — no need to repeat it on the compound parts.
   */
  variant?: AccordionVariant
  /**
   * Trigger row height, propagated to `Trigger` and `Title` via context.
   * `large` is the 48px touch row used inside the mobile drawers.
   */
  size?: AccordionSize
  /**
   * Accessible name for the accordion's `region` landmark. Base UI marks the
   * ROOT `role="region"`; with several accordions on one page the landmarks
   * are indistinguishable without a name (axe `landmark-unique`).
   */
  'aria-label'?: string
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  disabled?: boolean
  className?: string
  children?: ReactNode
  ref?: Ref<HTMLDivElement>
}

const toControlledValue = (open: boolean | undefined): string[] | undefined => {
  if (open === undefined) {
    return undefined
  }

  return open ? [SINGLE_ITEM_VALUE] : []
}

export const Root = ({
  variant = 'inline',
  size = 'medium',
  'aria-label': ariaLabel,
  defaultOpen,
  open,
  onOpenChange,
  disabled,
  className,
  children,
  ref,
}: RootProps) => {
  const { root } = accordionVariants({ variant, size })

  const value = toControlledValue(open)
  const defaultValue = defaultOpen === true ? [SINGLE_ITEM_VALUE] : []

  const handleValueChange = useEvent((next: (string | null)[]) => {
    onOpenChange?.(next.includes(SINGLE_ITEM_VALUE))
  })

  return (
    <AccordionVariantContext value={variant}>
      <AccordionSizeContext value={size}>
        <BaseAccordion.Root
          ref={ref}
          aria-label={ariaLabel}
          className={cn(root(), className)}
          defaultValue={defaultValue}
          disabled={disabled}
          value={value}
          onValueChange={handleValueChange}
        >
          <BaseAccordion.Item value={SINGLE_ITEM_VALUE}>{children}</BaseAccordion.Item>
        </BaseAccordion.Root>
      </AccordionSizeContext>
    </AccordionVariantContext>
  )
}
